import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { generateQRCode, downloadQRCode } from '../utils/qrcode';

const { FiX, FiDownload, FiLoader } = FiIcons;

const QRCodeModal = ({ isOpen, onClose, url, title }) => {
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && url) {
      generateQR();
    }
  }, [isOpen, url]);

  const generateQR = async () => {
    setLoading(true);
    setError('');
    
    try {
      const qrDataURL = await generateQRCode(url, {
        width: 300,
        margin: 3,
        color: {
          dark: '#065f46',
          light: '#ffffff'
        }
      });
      setQrCode(qrDataURL);
    } catch (err) {
      setError('Failed to generate QR code');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (qrCode) {
      const fileName = `qr-code-${url.replace(/[^a-zA-Z0-9]/g, '-')}.png`;
      downloadQRCode(qrCode, fileName);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-800">QR Code</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>

          {/* QR Code Display */}
          <div className="text-center">
            {loading && (
              <div className="flex items-center justify-center h-64">
                <SafeIcon icon={FiLoader} className="text-4xl text-emerald-500 animate-spin" />
              </div>
            )}

            {error && (
              <div className="text-red-600 p-4">
                {error}
              </div>
            )}

            {qrCode && !loading && (
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg border-2 border-gray-100 inline-block">
                  <img 
                    src={qrCode} 
                    alt="QR Code" 
                    className="w-64 h-64 mx-auto"
                  />
                </div>
                
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Scan to visit:</p>
                  <p className="font-mono text-sm text-emerald-700 bg-emerald-50 px-3 py-2 rounded-lg inline-block">
                    https://{url}
                  </p>
                </div>

                <motion.button
                  onClick={handleDownload}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3 px-6 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiDownload} />
                  <span>Download PNG</span>
                </motion.button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QRCodeModal;